import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { NavLink, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'

import { useMsal } from '@azure/msal-react'
import CloseIcon from '@mui/icons-material/Close'
import ControlCameraIcon from '@mui/icons-material/ControlCamera'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import LogoutIcon from '@mui/icons-material/Logout'
import MenuIcon from '@mui/icons-material/Menu'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'

import { getKmsVersionAsync } from '../../../api/kmsVersionApi'
import signInIcon from '../../../assets/person_edit.png'
import signInActiveIcon from '../../../assets/person_edit_active.png'
import icon from '../../../assets/sure_admin_icon.png'
import { IRootState } from '../../../redux/reducers'
import { ConfigurationState } from '../../../redux/reducers/initialState'
import { useIdentity } from '../identity/useIdentity'
import { usePermissions } from '../permission/usePermissions'
import { isNavItem, NavItem, NavItemGroup } from '../types'

import { links } from './InfoMenu'

import '../../../css/common/header/SlideInMenu.css'

type SlideInMenuProps = {
  navItems: (NavItem | NavItemGroup)[]
  hasAppConfigurationError?: boolean
}

const SlideInMenu: React.FC<SlideInMenuProps> = ({
  navItems,
  hasAppConfigurationError,
}): JSX.Element => {
  const { accounts } = useMsal()
  const { t } = useTranslation()
  const identity = useSelector((state: IRootState) => state.identity)
  const configuration: ConfigurationState | null = useSelector(
    (state: IRootState) => state.configuration,
  )
  const [isOpen, setIsOpen] = useState(false)
  const [openGroups, setOpenGroups] = useState<{ [key: string]: boolean }>({})
  const [lastClicked, setLastClicked] = useState<string | null>(null)
  const [navMap, setNavMap] = useState<Map<string, string[]>>(new Map())
  const [version, setVersion] = useState('unknown')
  const menuRef = useRef<HTMLDivElement>(null)
  const location = useLocation()
  const { getUserPermissions } = usePermissions()
  const { signOut, signIn, signInSilent } = useIdentity()

  let startX = 0

  const handleItemClick = (id: string): void => {
    setLastClicked(id)
    closeMenu()
  }

  const setAndToggleClickedItem = (id: string): void => {
    setLastClicked(id)
    toggleGroup(id)
  }

  const closeMenu = (): void => {
    startX = 0
    setIsOpen(false)
  }

  const openMenu = (): void => {
    const id = getNavIdOfCurrentOpenTab(navItems, location.pathname)
    if (id) {
      setLastClicked(id)
      toggleGroup(id)
    }
    setIsOpen(true)
  }

  const handleSignOut = (): void => {
    setAndToggleClickedItem('sign-in')
    if (identity) {
      signOut()
    }
  }

  const handleSignIn = (): void => {
    setAndToggleClickedItem('sign-out')
    signIn()
  }

  const handleTouchStart = (e: TouchEvent): void => {
    startX = e.touches[0].clientX
  }

  const handleTouchMove = (e: TouchEvent): void => {
    const touchX = e.touches[0].clientX
    if (startX - touchX > 150) {
      closeMenu()
    }
  }

  const handleMouseDown = (e: MouseEvent): void => {
    startX = e.clientX
  }

  const handleMouseMove = (e: MouseEvent): void => {
    if (startX - e.clientX > 150) {
      closeMenu()
    }
  }

  const handleClickOutside = (e: MouseEvent | TouchEvent): void => {
    if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
      closeMenu()
    }
  }

  useEffect(() => {
    const menu = document.querySelector('.slide-in-menu')
    if (menu) {
      menu.addEventListener('touchstart', handleTouchStart as EventListener)
      menu.addEventListener('touchmove', handleTouchMove as EventListener)
      menu.addEventListener('mousedown', handleMouseDown as EventListener)
      menu.addEventListener('mousemove', handleMouseMove as EventListener)
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('touchstart', handleClickOutside)

    return () => {
      if (menu) {
        menu.removeEventListener(
          'touchstart',
          handleTouchStart as EventListener,
        )
        menu.removeEventListener('touchmove', handleTouchMove as EventListener)
        menu.removeEventListener('mousedown', handleMouseDown as EventListener)
        menu.removeEventListener('mousemove', handleMouseMove as EventListener)
      }

      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('touchstart', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    if (accounts.length > 0) {
      signInSilent()
    }
  }, [accounts])

  useEffect(() => {
    if (identity !== null && identity !== undefined) {
      getUserPermissions()
    } else {
      signInSilent()
    }
  }, [identity])

  useEffect(() => {
    const fetchVersion = async () => {
      if (configuration !== null) {
        try {
          const data = await getKmsVersionAsync()
          if (data) {
            setVersion(data.toString())
          }
        } catch (error) {
          toast.error(t('version_fetch_failed'))
        }
      }
    }

    fetchVersion()
  }, [configuration])

  useEffect(() => {
    const computeNavMap = (
      items: (NavItem | NavItemGroup)[],
      parentIds: string[] = [],
    ) => {
      const map = new Map<string, string[]>()

      const traverse = (
        items: (NavItem | NavItemGroup)[],
        parentIds: string[],
      ) => {
        items.forEach((item) => {
          map.set(item.id, [...parentIds])
          if (!isNavItem(item)) {
            traverse(item.items, [...parentIds, item.id])
          }
        })
      }

      traverse(items, parentIds)
      return map
    }

    const map = computeNavMap(navItems)
    setNavMap(map)
  }, [navItems])

  const getNavIdOfCurrentOpenTab = (
    navItems: (NavItem | NavItemGroup)[],
    currentPath: string,
  ): string | null => {
    if (currentPath === '/') {
      return 'localaccesskeys'
    }
    for (const item of navItems) {
      if (isNavItem(item)) {
        if (item.to === currentPath) {
          return item.id
        }
      } else {
        const id = getNavIdOfCurrentOpenTab(item.items, currentPath)
        if (id) {
          return id
        }
      }
    }

    return null
  }

  const toggleGroup = (id: string): void => {
    setLastClicked(id)
    setOpenGroups((prev) => {
      const newOpenGroups = { ...prev }
      const parentIds = navMap.get(id) || []

      Object.keys(newOpenGroups).forEach((key) => {
        newOpenGroups[key] = false
      })

      // Open the specified group and its parents
      parentIds.forEach((parentId) => {
        newOpenGroups[parentId] = true
      })
      newOpenGroups[id] = !prev[id]

      return newOpenGroups
    })
  }

  const dropdownItemIcon = (
    item: NavItem | NavItemGroup,
    level = 0,
  ): JSX.Element => {
    if (level === 0) {
      return (
        <span style={{ marginLeft: '5vw', marginRight: '10vw' }}>
          {item.icon}
        </span>
      )
    } else if (level === 1) {
      return (
        <span style={{ marginLeft: '5vw', marginRight: '10vw' }}>
          <ControlCameraIcon style={iconStyle} />
        </span>
      )
    } else {
      return <span style={{ marginLeft: '5vw', marginRight: '10vw' }}>-</span>
    }
  }

  const renderNavItems = (
    items: (NavItem | NavItemGroup)[],
    level = 0,
  ): JSX.Element[] => {
    return items.map((item) => {
      if (isNavItem(item)) {
        return (
          <NavLink
            key={item.to}
            to={item.to}
            className={`slide-in-menu-dropdown-item ${
              lastClicked === item.id ? 'last-clicked' : ''
            }`}
            onClick={() => handleItemClick(item.id)}
            style={{ ...menuDropDownItemStyle, paddingLeft: `${level * 50}px` }}
          >
            {dropdownItemIcon(item, level)}
            <span>{item.label}</span>
          </NavLink>
        )
      } else {
        const isOpen = openGroups[item.id] || false
        return (
          <>
            <button
              className={`slide-in-menu-dropdown-item ${
                lastClicked === item.id ? 'last-clicked' : ''
              }`}
              onClick={() => toggleGroup(item.id)}
              style={{
                ...menuDropDownItemStyle,
                paddingLeft: `${level * 50}px`,
              }}
            >
              {dropdownItemIcon(item, level)}
              <span>{item.name}</span>
              {!isOpen && (
                <span style={{ marginLeft: 'auto' }}>
                  <ExpandMoreIcon style={iconStyle} />
                </span>
              )}
              {isOpen && (
                <span style={{ marginLeft: 'auto' }}>
                  <ExpandLessIcon style={iconStyle} />
                </span>
              )}
            </button>
            {isOpen && <>{renderNavItems(item.items, level + 1)}</>}
          </>
        )
      }
    })
  }

  const renderUserInfo = (): JSX.Element => {
    return (
      <span
        className="slide-in-menu-dropdown-item-no-hover"
        style={{ marginTop: 'auto', paddingLeft: '0' }}
      >
        <span style={{ marginLeft: '5vw', marginRight: '10vw' }}>
          <PersonOutlineIcon style={iconStyle} />
        </span>
        <span>{identity?.name}</span>
      </span>
    )
  }

  const renderFaq = (): JSX.Element => {
    return (
      <a
        href={links[0].href}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          paddingLeft: '0',
          textDecoration: 'none',
          color: 'inherit',
          ...(identity ? {} : { marginTop: 'auto' }),
          ...(lastClicked === 'faq' ? { color: ' #02476b' } : {}),
        }}
        onClick={() => setAndToggleClickedItem('faq')}
        className={`slide-in-menu-dropdown-item ${
          lastClicked === 'faq' ? 'last-clicked' : ''
        }`}
      >
        <span style={{ marginLeft: '5vw', marginRight: '10vw' }}>
          <HelpOutlineIcon style={iconStyle} />
        </span>
        <span>{t('faq')}</span>
      </a>
    )
  }

  const renderReleaseHistory = (): JSX.Element => {
    return (
      <a
        href={links[1].href}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          textDecoration: 'none',
          color: 'inherit',
          paddingLeft: '0',
          ...(lastClicked === 'release-history' ? { color: ' #02476b' } : {}),
        }}
        onClick={() => setAndToggleClickedItem('release-history')}
        className={`slide-in-menu-dropdown-item ${
          lastClicked === 'release-history' ? 'last-clicked' : ''
        }`}
      >
        <span style={{ marginLeft: '5vw', marginRight: '10vw' }}>
          <InfoOutlinedIcon style={iconStyle} />
        </span>
        <span>{t('release_history')}</span>
      </a>
    )
  }

  const renderSignOut = (): JSX.Element => {
    return (
      <button
        onClick={handleSignOut}
        style={{ paddingLeft: '0', display: 'flex', alignItems: 'center' }}
        className={`slide-in-menu-dropdown-item ${
          lastClicked === 'sign-in' ? 'last-clicked' : ''
        }`}
      >
        <span style={{ marginLeft: '5vw', marginRight: '10vw' }}>
          <LogoutIcon style={iconStyle} />
        </span>
        <span>{t('sign_out')}</span>
      </button>
    )
  }

  const renderSignIn = (): JSX.Element => {
    return (
      <button
        className={`slide-in-menu-dropdown-item ${
          lastClicked === 'sign-out' ? 'last-clicked' : ''
        }`}
        onClick={handleSignIn}
        style={{ paddingLeft: '0', display: 'flex', alignItems: 'center' }}
      >
        <span style={{ marginLeft: '5vw', marginRight: '10vw' }}>
          <img
            alt="Sign In Icon"
            src={lastClicked === 'sign-out' ? signInActiveIcon : signInIcon}
            style={{
              height: 'min(5vw, 5vh)',
              width: 'min(5vw, 5vh)',
            }}
          />
        </span>
        <span>{t('sign_in')}</span>
      </button>
    )
  }

  const renderVersionInfo = (): JSX.Element => {
    return (
      <span
        className="slide-in-menu-dropdown-item-no-hover"
        style={{ paddingLeft: '0' }}
      >
        <span style={{ marginLeft: '5vw', marginRight: '10vw' }}>
          <InfoOutlinedIcon style={iconStyle} />
        </span>
        <span>
          {t('version')} {version === 'unknown' ? t('unknown') : version}
        </span>
      </span>
    )
  }

  return (
    <>
      <button onClick={openMenu} style={menuButtonStyle}>
        <MenuIcon style={menuIconStyle} />
      </button>
      {isOpen && <div className="overlay" onClick={closeMenu}></div>}
      <div ref={menuRef} className={`slide-in-menu ${isOpen ? 'open' : ''}`}>
        {isOpen && (
          <div style={menuHeaderStyle}>
            <div style={{ flex: '0 0 5%', textAlign: 'left' }}>
              <img
                alt="KMS Logo"
                src={icon}
                className="d-inline-block align-top responsive-icon"
                style={{ margin: '10px', height: 'calc(7vw + 2.5vh)' }}
              />
            </div>
            <div style={menuTitleTextStyle}>{t('kms_admin_portal')}</div>
            <div style={{ flex: '0 0 5%', textAlign: 'right' }}>
              <CloseIcon onClick={closeMenu} style={menuTitleCloseIconStyle} />
            </div>
          </div>
        )}
        <div
          style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
        >
          {renderNavItems(navItems)}
          {identity && renderUserInfo()}
          {renderFaq()}
          {renderReleaseHistory()}
          {identity && !hasAppConfigurationError && renderSignOut()}
          {!identity && !hasAppConfigurationError && renderSignIn()}
          {renderVersionInfo()}
        </div>
      </div>
    </>
  )
}

export default SlideInMenu

const menuButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  margin: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 'calc(5vw + 1vh)',
  height: 'calc(5vw + 1vh)',
  marginInline: '3vw',
}

const menuIconStyle: React.CSSProperties = {
  color: '#FFFFFF',
  width: '100%',
  height: '100%',
}

const menuHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  marginTop: '30px',
  marginBottom: '30px',
  marginLeft: '20px',
  width: '80%',
}

const menuDropDownItemStyle: React.CSSProperties = {
  display: 'flex',
  maxWidth: '100%',
  alignItems: 'center',
  textAlign: 'left',
  whiteSpace: 'normal',
  wordWrap: 'break-word',
}

const iconStyle: React.CSSProperties = {
  height: '5vh',
  width: '5vw',
}

const menuTitleTextStyle: React.CSSProperties = {
  flex: '0 0 90%',
  textAlign: 'left',
  fontWeight: 500,
  fontSize: 'calc(5vw + 1vh)',
  paddingLeft: '10px',
}

const menuTitleCloseIconStyle: React.CSSProperties = {
  color: '#FFFFFF',
  width: 'calc(5vw + 1vh)',
  height: 'calc(5vw + 1vh)',
}
